import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Loader2, X, Search, CheckCheck, XCircle, Globe, CheckSquare } from "lucide-react";
import { fetchDistinctValues, exportLeadsToExcel } from "../../services/crmServices/leadService";

// Props:
//   open, onClose
//   selectedLeads — array of lead objects ({ _id, ... }) currently checkboxed in the parent table.
//                   When non-empty, modal defaults to "Export selected only".
export function ExportLeadsModal({ open, onClose, selectedLeads = [] }) {
    const selectedLeadIds = selectedLeads.map((l) => l._id).filter(Boolean);
    const hasSelection = selectedLeadIds.length > 0;

    // Mode: "selected" (only the marked rows) | "filter" (by source / country / all)
    const [mode, setMode] = useState("filter");

    // Sources
    const [sources, setSources] = useState([]);
    const [selectedSources, setSelectedSources] = useState([]);
    const [sourceSearch, setSourceSearch] = useState("");
    const [loadingSources, setLoadingSources] = useState(false);
    const sourceSearchRef = useRef(null);

    // Countries
    const [countries, setCountries] = useState([]);
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [countrySearch, setCountrySearch] = useState("");
    const [loadingCountries, setLoadingCountries] = useState(false);
    const countrySearchRef = useRef(null);

    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        if (open) {
            loadSources();
            loadCountries();
            setSelectedSources([]);
            setSelectedCountries([]);
            setSourceSearch("");
            setCountrySearch("");
            // Default mode: if user came in with selected rows, start in "selected" mode
            setMode(hasSelection ? "selected" : "filter");
        }
    }, [open, hasSelection]);

    const loadSources = async () => {
        setLoadingSources(true);
        try {
            const res = await fetchDistinctValues("source");
            setSources(res?.data || []);
        } catch {
            toast.error("Failed to load sources");
        } finally {
            setLoadingSources(false);
        }
    };

    const loadCountries = async () => {
        setLoadingCountries(true);
        try {
            const res = await fetchDistinctValues("country");
            setCountries(res?.data || []);
        } catch {
            toast.error("Failed to load countries");
        } finally {
            setLoadingCountries(false);
        }
    };

    // --- Sources handlers ---
    const filteredSources = sources.filter(
        (s) => s && s.toLowerCase().includes(sourceSearch.toLowerCase())
    );
    const unselectedSources = filteredSources.filter((s) => !selectedSources.includes(s));

    const handleSelectSource = (source) => {
        if (!selectedSources.includes(source)) {
            setSelectedSources((prev) => [...prev, source]);
        }
        setSourceSearch("");
        sourceSearchRef.current?.focus();
    };
    const handleRemoveSource = (source) => setSelectedSources((prev) => prev.filter((s) => s !== source));
    const handleSelectAllSources = () => { setSelectedSources([...sources]); setSourceSearch(""); };
    const handleClearSources = () => setSelectedSources([]);

    // --- Countries handlers ---
    const filteredCountries = countries.filter(
        (c) => c && c.toLowerCase().includes(countrySearch.toLowerCase())
    );
    const unselectedCountries = filteredCountries.filter((c) => !selectedCountries.includes(c));

    const handleSelectCountry = (country) => {
        if (!selectedCountries.includes(country)) {
            setSelectedCountries((prev) => [...prev, country]);
        }
        setCountrySearch("");
        countrySearchRef.current?.focus();
    };
    const handleRemoveCountry = (country) => setSelectedCountries((prev) => prev.filter((c) => c !== country));
    const handleSelectAllCountries = () => { setSelectedCountries([...countries]); setCountrySearch(""); };
    const handleClearCountries = () => setSelectedCountries([]);

    // --- Export ---
    const handleExport = async () => {
        if (mode === "selected") {
            if (selectedLeadIds.length === 0) {
                toast.error("No leads selected");
                return;
            }
            setExporting(true);
            try {
                await exportLeadsToExcel([], [], selectedLeadIds);
                toast.success(`Exported ${selectedLeadIds.length} selected lead(s)`);
                onClose();
            } catch {
                toast.error("Failed to export leads. Please try again.");
            } finally {
                setExporting(false);
            }
            return;
        }

        // filter mode
        if (selectedSources.length === 0 && selectedCountries.length === 0) {
            toast.error("Please select at least one source or country");
            return;
        }
        setExporting(true);
        try {
            await exportLeadsToExcel(selectedSources, selectedCountries, []);
            const parts = [];
            if (selectedSources.length > 0) parts.push(`${selectedSources.length} source(s)`);
            if (selectedCountries.length > 0) parts.push(`${selectedCountries.length} country(ies)`);
            toast.success(`Export started — ${parts.join(", ")}`);
            onClose();
        } catch {
            toast.error("Failed to export leads. Please try again.");
        } finally {
            setExporting(false);
        }
    };

    const handleExportAll = async () => {
        setExporting(true);
        try {
            await exportLeadsToExcel([], [], []);
            toast.success("Exporting all leads — download started");
            onClose();
        } catch {
            toast.error("Failed to export leads. Please try again.");
        } finally {
            setExporting(false);
        }
    };

    const hasFilters = selectedSources.length > 0 || selectedCountries.length > 0;
    const canSubmit = mode === "selected" ? selectedLeadIds.length > 0 : hasFilters;

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
                <DialogHeader className="border-b pb-3">
                    <DialogTitle className="flex items-center text-lg font-semibold text-gray-900">
                        <FileSpreadsheet className="h-5 w-5 mr-2 text-green-600" />
                        Export Leads to Excel
                    </DialogTitle>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto p-4 space-y-5">

                    {/* ── Mode toggle (shown only when leads are checkboxed in the table) ── */}
                    {hasSelection && (
                        <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-lg p-1 flex gap-1">
                            <button
                                onClick={() => setMode("selected")}
                                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-semibold transition-all ${
                                    mode === "selected"
                                        ? "bg-white text-emerald-700 shadow-sm border border-emerald-200"
                                        : "text-gray-600 hover:bg-white/60"
                                }`}
                            >
                                <CheckSquare className="h-3.5 w-3.5" />
                                Selected only
                                <span className={`ml-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                    mode === "selected" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"
                                }`}>
                                    {selectedLeadIds.length}
                                </span>
                            </button>
                            <button
                                onClick={() => setMode("filter")}
                                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-semibold transition-all ${
                                    mode === "filter"
                                        ? "bg-white text-gray-800 shadow-sm border border-gray-200"
                                        : "text-gray-600 hover:bg-white/60"
                                }`}
                            >
                                <Globe className="h-3.5 w-3.5" />
                                By source / country
                            </button>
                        </div>
                    )}

                    {/* ── Selected mode info card ── */}
                    {mode === "selected" && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
                            <div className="p-2 bg-emerald-100 rounded-md">
                                <CheckSquare className="h-4 w-4 text-emerald-700" />
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-emerald-900">
                                    Exporting {selectedLeadIds.length} selected lead{selectedLeadIds.length === 1 ? "" : "s"}
                                </p>
                                <p className="text-xs text-emerald-700 mt-0.5">
                                    Only the leads you've ticked in the table will be included. Country & source filters are ignored in this mode.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* ── Filter sections (only in filter mode) ── */}
                    {mode === "filter" && (
                    <>
                    {/* ── Country Filter ── */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-1.5">
                            <Globe className="h-3.5 w-3.5 text-blue-600" />
                            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Filter by Country</span>
                        </div>

                        {/* Selected countries tags */}
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label className="text-xs text-gray-600">
                                    Selected
                                    {selectedCountries.length > 0 && (
                                        <span className="ml-1.5 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-bold">
                                            {selectedCountries.length}
                                        </span>
                                    )}
                                </label>
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleSelectAllCountries}
                                        disabled={loadingCountries || selectedCountries.length === countries.length}
                                        className="text-[11px] text-blue-600 hover:text-blue-800 disabled:opacity-40 flex items-center gap-0.5"
                                    >
                                        <CheckCheck className="h-3 w-3" />
                                        All
                                    </button>
                                    <button
                                        onClick={handleClearCountries}
                                        disabled={selectedCountries.length === 0}
                                        className="text-[11px] text-red-500 hover:text-red-700 disabled:opacity-40 flex items-center gap-0.5"
                                    >
                                        <XCircle className="h-3 w-3" />
                                        Clear
                                    </button>
                                </div>
                            </div>
                            <div className="min-h-[40px] p-2 border border-gray-200 rounded-lg bg-gray-50 flex flex-wrap gap-1.5">
                                {selectedCountries.length === 0 ? (
                                    <span className="text-xs text-gray-400 self-center px-1">
                                        No countries selected — search and click to add
                                    </span>
                                ) : (
                                    selectedCountries.map((country) => (
                                        <span
                                            key={country}
                                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 border border-blue-300 rounded text-xs font-medium"
                                        >
                                            {country}
                                            <button
                                                onClick={() => handleRemoveCountry(country)}
                                                className="hover:text-red-600 transition-colors"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </span>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Country search */}
                        <div>
                            <label className="text-xs font-semibold text-gray-700 mb-1.5 block">
                                Search & Add Countries
                            </label>
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                <Input
                                    ref={countrySearchRef}
                                    value={countrySearch}
                                    onChange={(e) => setCountrySearch(e.target.value)}
                                    placeholder={loadingCountries ? "Loading countries..." : `Search ${countries.length} countries...`}
                                    className="pl-8 h-9 text-sm"
                                    disabled={loadingCountries}
                                />
                            </div>
                            {countrySearch.length > 0 && (
                                <div className="mt-1 border border-gray-200 rounded-lg bg-white shadow-sm max-h-40 overflow-y-auto">
                                    {unselectedCountries.length === 0 ? (
                                        <p className="text-xs text-gray-400 text-center py-3">
                                            {filteredCountries.length === 0 ? "No countries match" : "All matching countries selected"}
                                        </p>
                                    ) : (
                                        unselectedCountries.map((country) => (
                                            <button
                                                key={country}
                                                onClick={() => handleSelectCountry(country)}
                                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-800 border-b border-gray-100 last:border-0 transition-colors"
                                            >
                                                {country}
                                            </button>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="border-t border-gray-100" />

                    {/* ── Source Filter ── */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-1.5">
                            <FileSpreadsheet className="h-3.5 w-3.5 text-green-600" />
                            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Filter by Source</span>
                        </div>

                        {/* Selected sources tags */}
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label className="text-xs text-gray-600">
                                    Selected
                                    {selectedSources.length > 0 && (
                                        <span className="ml-1.5 px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-bold">
                                            {selectedSources.length}
                                        </span>
                                    )}
                                </label>
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleSelectAllSources}
                                        disabled={loadingSources || selectedSources.length === sources.length}
                                        className="text-[11px] text-blue-600 hover:text-blue-800 disabled:opacity-40 flex items-center gap-0.5"
                                    >
                                        <CheckCheck className="h-3 w-3" />
                                        All
                                    </button>
                                    <button
                                        onClick={handleClearSources}
                                        disabled={selectedSources.length === 0}
                                        className="text-[11px] text-red-500 hover:text-red-700 disabled:opacity-40 flex items-center gap-0.5"
                                    >
                                        <XCircle className="h-3 w-3" />
                                        Clear
                                    </button>
                                </div>
                            </div>
                            <div className="min-h-[40px] p-2 border border-gray-200 rounded-lg bg-gray-50 flex flex-wrap gap-1.5">
                                {selectedSources.length === 0 ? (
                                    <span className="text-xs text-gray-400 self-center px-1">
                                        No sources selected — search and click to add
                                    </span>
                                ) : (
                                    selectedSources.map((source) => (
                                        <span
                                            key={source}
                                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 border border-green-300 rounded text-xs font-medium"
                                        >
                                            {source}
                                            <button
                                                onClick={() => handleRemoveSource(source)}
                                                className="hover:text-red-600 transition-colors"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </span>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Source search */}
                        <div>
                            <label className="text-xs font-semibold text-gray-700 mb-1.5 block">
                                Search & Add Sources
                            </label>
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                <Input
                                    ref={sourceSearchRef}
                                    value={sourceSearch}
                                    onChange={(e) => setSourceSearch(e.target.value)}
                                    placeholder={loadingSources ? "Loading sources..." : `Search ${sources.length} sources...`}
                                    className="pl-8 h-9 text-sm"
                                    disabled={loadingSources}
                                />
                            </div>
                            {/* Dropdown — only when user has typed something */}
                            {sourceSearch.length > 0 && (
                                <div className="mt-1 border border-gray-200 rounded-lg bg-white shadow-sm max-h-40 overflow-y-auto">
                                    {unselectedSources.length === 0 ? (
                                        <p className="text-xs text-gray-400 text-center py-3">
                                            {filteredSources.length === 0 ? "No sources match" : "All matching sources selected"}
                                        </p>
                                    ) : (
                                        unselectedSources.map((source) => (
                                            <button
                                                key={source}
                                                onClick={() => handleSelectSource(source)}
                                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-800 border-b border-gray-100 last:border-0 transition-colors"
                                            >
                                                {source}
                                            </button>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    </>
                    )}
                </div>

                <div className="border-t pt-3 px-4 pb-4 flex items-center justify-between gap-2">
                    {mode === "filter" ? (
                        <button
                            onClick={handleExportAll}
                            disabled={exporting}
                            className="text-xs text-gray-500 hover:text-gray-700 underline disabled:opacity-50"
                        >
                            Export all leads (no filter)
                        </button>
                    ) : (
                        <span className="text-[11px] text-gray-400">
                            {selectedLeadIds.length} lead{selectedLeadIds.length === 1 ? "" : "s"} will be exported
                        </span>
                    )}
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={onClose} disabled={exporting}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleExport}
                            disabled={exporting || !canSubmit}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                        >
                            {exporting ? (
                                <>
                                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                    Exporting...
                                </>
                            ) : (
                                <>
                                    <Download className="h-3.5 w-3.5 mr-1.5" />
                                    {mode === "selected"
                                        ? `Export Selected (${selectedLeadIds.length})`
                                        : `Export${hasFilters ? ` (${selectedSources.length + selectedCountries.length})` : ""}`}
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
